/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import { TimeFormat } from "../../../../forms/elements/timeInputUtils";
import { DARK_GREY, RED_CREEK } from "../../../../styles/colors";
import { TimeRangeIssue, validateTimeRange } from "./timeRange.validation";
import { getResource } from "../../../../language/resources/getResource";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TimeInput } from "../../../../forms/elements/TimeInput";
import { ValidationIndicator } from "../../validation/ValidationIndicator";

interface TimeRangeInputProps {
  id?: string;
  resources: Record<string, any>;
  start: Date | null;
  startPlaceholder?: string;
  end: Date | null;
  endPlaceholder?: string;
  title?: string;
  onChange: (range: { start: Date | null; end: Date | null }) => void;
  format: TimeFormat;
  showDash?: boolean;
  rootCss?: SerializedStyles;
  timeInputCss?: SerializedStyles;
  forceValidationVersion?: number;
  clearValidation?: boolean;
}

const dashCss = css`
  flex: 0 0 auto;
  width: 10px;
  height: 2px;
  background-color: ${DARK_GREY};
  margin: 0 9px;
`;

const Dash = () => <span css={dashCss} />;

const rowCss = css`
  display: flex;
  align-items: center;
`;

const errorInputCss = css`
  box-shadow: inset 0 0 0 2px ${RED_CREEK};

  &:focus {
    box-shadow: inset 0 0 0 2px ${RED_CREEK};
  }
`;

// Future if warnings are required
// const warningInputCss = css`
//   box-shadow: inset 0 0 0 2px ${SUNSET_GOLD};

//   &:focus {
//     box-shadow: inset 0 0 0 2px ${SUNSET_GOLD};
//   }
// `;

function mapTimeRangeIssueToMessage(
  issue: TimeRangeIssue | undefined,
  resources: Record<string, any>,
): string | undefined {
  if (!issue) {
    return undefined;
  }

  switch (issue.code) {
    case "START_AND_END_REQUIRED":
      return getResource(resources, "TIME_RANGE_INPUT_START_END_REQUIRED");

    case "START_REQUIRED":
      return getResource(resources, "TIME_RANGE_INPUT_START_REQUIRED");

    case "END_REQUIRED":
      return getResource(resources, "TIME_RANGE_INPUT_END_REQUIRED");

    case "START_AFTER_END":
      return getResource(resources, "TIME_RANGE_INPUT_START_BEFORE_END");

    default:
      return getResource(
        resources,
        "TIME_RANGE_INPUT_UNKNOWN_VALIDATION_ERROR",
      );
  }
}

function getBorderStyleForField(
  field: "start" | "end",
  issues: TimeRangeIssue[],
): SerializedStyles | undefined {
  const relevantIssues = issues.filter(
    (issue) => issue.field === field || issue.field === "range",
  );

  if (relevantIssues.some((i) => i.severity === "error")) {
    return errorInputCss;
  }

  return undefined;
}

export function TimeRangeInput({
  id,
  resources,
  start,
  startPlaceholder,
  end,
  endPlaceholder,
  title,
  onChange,
  format,
  showDash = true,
  rootCss,
  timeInputCss,
  forceValidationVersion,
  clearValidation,
}: TimeRangeInputProps) {
  const [touched, setTouched] = useState(false);
  const [validateOnSubmit, setValidateOnSubmit] = useState(false);

  const shouldValidate =
    validateOnSubmit || touched || start !== null || end !== null;

  const issues = useMemo(() => {
    if (!shouldValidate) return [];
    return validateTimeRange(start, end);
  }, [start, end, shouldValidate]);

  const primaryIssue = issues[0] ?? undefined;

  const markTouched = useCallback(() => {
    setTouched(true);
  }, []);

  const startBorderCss = useMemo(
    () => getBorderStyleForField("start", issues),
    [issues],
  );

  const endBorderCss = useMemo(
    () => getBorderStyleForField("end", issues),
    [issues],
  );

  useEffect(() => {
    if ((forceValidationVersion ?? 0) > 0) {
      setValidateOnSubmit(true);
    }
  }, [forceValidationVersion]);

  // Clear validation ONLY if both start and end are empty
  useEffect(() => {
    if (clearValidation && start === null && end === null) {
      setTouched(false);
      setValidateOnSubmit(false);
    }
  }, [clearValidation, start, end]);

  return (
    <div css={rootCss}>
      <div css={rowCss}>
        <TimeInput
          id={`${id}-timerange-start-input`}
          value={start}
          format={format}
          title={title}
          placeholder={startPlaceholder}
          rootCss={timeInputCss}
          inputCss={startBorderCss}
          onChange={(next) => {
            markTouched();
            onChange({ start: next, end });
          }}
          onBlur={() => markTouched()}
        />

        {showDash && <Dash />}

        <TimeInput
          id={`${id}-timerange-end-input`}
          value={end}
          format={format}
          title={title}
          placeholder={endPlaceholder}
          rootCss={timeInputCss}
          inputCss={endBorderCss}
          onChange={(next) => {
            markTouched();
            onChange({ start, end: next });
          }}
          onBlur={() => markTouched()}
        />
        {
          <ValidationIndicator
            severity="error"
            message={mapTimeRangeIssueToMessage(primaryIssue, resources)}
            visible={!!primaryIssue}
          />
        }
      </div>
    </div>
  );
}
